import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Video, CalendarDays, CheckCircle2, Phone } from "lucide-react";
import { getLocalSessions, saveLocalSessions } from "@/lib/therapists";

const TIPS = ["Find a quiet and comfortable space", "Ensure a stable internet connection", "Join 5 minutes before the session", "Speak openly, this is a safe space"];
const MODE_LABEL = { video: "Video Consultation", audio: "Audio Consultation", in_person: "In-clinic Consultation" };

export default function WellnessSessions() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("upcoming");
  const [sessions, setSessions] = useState([]);

  useEffect(() => { setSessions(getLocalSessions()); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = sessions.filter((s) => (s.date || "") >= today && s.status !== "cancelled");
  const past = sessions.filter((s) => (s.date || "") < today || s.status === "cancelled");
  const shown = tab === "upcoming" ? upcoming : past;

  const cancel = (id) => {
    const next = sessions.map((s) => (s.id === id ? { ...s, status: "cancelled" } : s));
    setSessions(next); saveLocalSessions(next);
  };

  const fmtDate = (iso) => { if (!iso) return ""; return new Date(iso).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" }); };

  return (
    <div className="space-y-4">
      <button onClick={() => navigate("/mental-wellness")} className="flex items-center gap-1 text-sm font-medium" style={{ color: "#7C3AED" }} data-testid="sessions-back">
        <ArrowLeft className="h-4 w-4" /> Mental Wellness
      </button>
      <h1 className="text-2xl font-semibold" style={{ color: "#2E1065" }}>My Sessions</h1>

      <div className="flex gap-1 rounded-2xl p-1" style={{ background: "#EDE7F8" }}>
        {["upcoming", "past"].map((tb) => (
          <button key={tb} onClick={() => setTab(tb)} data-testid={`tab-${tb}`} className="flex-1 py-2.5 rounded-xl text-sm font-bold capitalize" style={tab === tb ? { background: "#7C3AED", color: "#fff" } : { color: "#8B7CA6" }}>{tb}</button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="text-center py-12">
          <CalendarDays className="h-10 w-10 mx-auto" style={{ color: "#C9B9E8" }} />
          <p className="text-muted-foreground mt-3">No {tab} sessions yet.</p>
          <button onClick={() => navigate("/mental-wellness/consultants")} className="mt-4 rounded-xl px-5 py-2.5 font-bold text-white" style={{ background: "#7C3AED" }}>Find a Therapist</button>
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((s) => (
            <div key={s.id} className="rounded-2xl border bg-white p-4" style={{ borderColor: "#EDE7F8" }} data-testid={`session-${s.id}`}>
              <p className="text-sm font-bold" style={{ color: "#7C3AED" }}>{fmtDate(s.date)}</p>
              <div className="flex items-center gap-3 mt-2.5">
                <img src={s.photo} alt="" className="h-14 w-14 rounded-2xl object-cover" style={{ background: "#EDE7F8" }} />
                <div className="flex-1 min-w-0">
                  <p className="font-bold" style={{ color: "#1F1147" }}>{s.consultant_name}</p>
                  <p className="text-sm" style={{ color: "#7C3AED" }}>{s.consultant_title}</p>
                  <div className="flex items-center gap-2 mt-1.5 text-xs" style={{ color: "#8B7CA6" }}>
                    <Clock className="h-3 w-3" /> {s.time}
                    <Video className="h-3 w-3 ml-1" /> {MODE_LABEL[s.mode] || "Session"}
                  </div>
                </div>
                <span className="rounded-full px-2.5 py-1 text-xs font-bold" style={s.status === "cancelled" ? { background: "#FEE2E2", color: "#B91C1C" } : { background: "#E7F6EC", color: "#16A34A" }}>{s.status === "cancelled" ? "Cancelled" : "Confirmed"}</span>
              </div>
              {tab === "upcoming" && s.status !== "cancelled" && (
                <div className="flex gap-2.5 mt-3.5">
                  <button onClick={() => navigate("/mental-wellness/consultants")} data-testid={`reschedule-${s.id}`} className="flex-1 rounded-xl py-2.5 text-sm font-bold border-2" style={{ borderColor: "#7C3AED", color: "#7C3AED" }}>Reschedule</button>
                  <button onClick={() => cancel(s.id)} data-testid={`cancel-${s.id}`} className="flex-1 rounded-xl py-2.5 text-sm font-bold border-2" style={{ borderColor: "#FCA5A5", color: "#DC2626" }}>Cancel</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "upcoming" && shown.length > 0 && (
        <div className="rounded-2xl p-4" style={{ background: "#F3EEFC" }}>
          <p className="font-bold mb-3" style={{ color: "#2E1065" }}>Session tips</p>
          {TIPS.map((tp) => (
            <div key={tp} className="flex items-center gap-2 mb-2"><CheckCircle2 className="h-4 w-4" style={{ color: "#7C3AED" }} /><span className="text-sm font-semibold" style={{ color: "#53456F" }}>{tp}</span></div>
          ))}
        </div>
      )}

      <a href="tel:9152987821" data-testid="helpline-call" className="flex items-center justify-between rounded-2xl border bg-white p-4" style={{ borderColor: "#F1ECFA" }}>
        <span className="text-sm font-semibold" style={{ color: "#1F1147" }}>iCALL (Mental Health Helpline)</span>
        <span className="flex items-center gap-2 rounded-lg px-3 py-2 text-white font-bold text-sm" style={{ background: "#16A34A" }}><Phone className="h-4 w-4" /> 9152987821</span>
      </a>
    </div>
  );
}
